import React, { useState, useEffect } from 'react';

/**
 * EncryptionKeyGate Component
 * 
 * Acts as a security gatekeeper before users can access the main website.
 * Requires users to enter a valid encryption key that is validated against the backend.
 * Only shows the main application content after successful validation (200 response).
 * 
 * Features:
 * - Brute force protection: 10 failed attempts = 180 minute lockout
 * - Debug mode bypass: Set DEBUG_MODE to true to skip the gate during development
 */
function EncryptionKeyGate({ children, onKeyValidated }) {
  // ============================================================================
  // DEBUG MODE CONTROL
  // ============================================================================
  // ⚠️  PRODUCTION DEPLOY REMINDER: This is currently set to TRUE for local development.
  //     Set to FALSE before every production build/deploy so users must enter a valid key.
  // true  = gate is bypassed entirely (dev/testing only)
  // false = users must enter valid encryption key (production)
  const DEBUG_MODE = false;
  
  // Basic state
  const [encryptionKey, setEncryptionKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Brute force protection state
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutEndTime, setLockoutEndTime] = useState(null);
  const [remainingLockoutTime, setRemainingLockoutTime] = useState(0);

  // API Configuration - same as AppointmentForm
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 
                       (typeof window !== 'undefined' ? `http://${window.location.hostname}:8000` : 'http://localhost:8000');

  // ============================================================================
  // BRUTE FORCE PROTECTION - LOCKOUT MANAGEMENT
  // ============================================================================
  
  // ============================================================================
  // PERSISTENT SESSION - Check for saved auth on mount
  // ============================================================================
  // If a valid, unexpired session is stored from a previous login, silently
  // re-validate the key against the backend and skip the gate if confirmed.
  // Session expires after 90 days or when the API key is revoked/changed.
  const SESSION_STORAGE_KEY = 'namhs_auth_session';
  const SESSION_EXPIRY_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

  useEffect(() => {
    const saved = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!saved) return;

    let parsed;
    try {
      parsed = JSON.parse(saved);
    } catch {
      localStorage.removeItem(SESSION_STORAGE_KEY);
      return;
    }

    const { key, expiresAt } = parsed;

    if (!key || !expiresAt || expiresAt <= Date.now()) {
      // Session missing or expired
      localStorage.removeItem(SESSION_STORAGE_KEY);
      return;
    }

    // Session found and not expired — silently re-validate against backend
    setIsValidating(true);
    fetch(`${API_BASE_URL}/api/auth/validate-key`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ api_key: key })
    })
      .then(res => {
        if (res.status === 200) {
          // Key still valid — grant access without showing the gate
          setIsAuthenticated(true);
          if (onKeyValidated) onKeyValidated(key);
          // Refresh expiry so active users never get logged out
          localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
            key,
            expiresAt: Date.now() + SESSION_EXPIRY_MS
          }));
        } else {
          // Key was revoked or changed on the backend — clear session, show gate
          localStorage.removeItem(SESSION_STORAGE_KEY);
        }
      })
      .catch(() => {
        // Network error — keep session in place, show gate so user can retry manually
        // Do NOT clear the session; the key may still be valid once the server is reachable
      })
      .finally(() => setIsValidating(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load lockout state from localStorage on mount
  useEffect(() => {
    const lockoutData = localStorage.getItem('namhs_gate_lockout');
    if (lockoutData) {
      try {
        const { endTime, attempts } = JSON.parse(lockoutData);
        const now = Date.now();
        
        if (endTime > now) {
          // Still locked out
          setIsLocked(true);
          setLockoutEndTime(endTime);
          setFailedAttempts(attempts);
          setRemainingLockoutTime(Math.ceil((endTime - now) / 1000));
        } else {
          // Lockout expired, clear it
          localStorage.removeItem('namhs_gate_lockout');
        }
      } catch (e) {
        // Invalid data, clear it
        localStorage.removeItem('namhs_gate_lockout');
      }
    }
  }, []);
  
  // Lockout countdown timer
  useEffect(() => {
    if (isLocked && lockoutEndTime) {
      const timer = setInterval(() => {
        const now = Date.now();
        const remaining = Math.ceil((lockoutEndTime - now) / 1000);
        
        if (remaining <= 0) {
          // Lockout expired
          setIsLocked(false);
          setLockoutEndTime(null);
          setFailedAttempts(0);
          setRemainingLockoutTime(0);
          localStorage.removeItem('namhs_gate_lockout');
          setError('');
        } else {
          setRemainingLockoutTime(remaining);
        }
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [isLocked, lockoutEndTime]);

  /**
   * Validate encryption key against backend
   * Sends a ping/validation request to check if the key is valid
   * Includes brute force protection (10 attempts = 180 minute lockout)
   */
  const validateEncryptionKey = async (e) => {
    e.preventDefault();
    
    // Check if locked out
    if (isLocked) {
      setError('You are now locked out. Please contact NAMHS IT Administrator.');
      return;
    }
    
    if (!encryptionKey.trim()) {
      setError('Please enter the encryption key');
      return;
    }

    setIsValidating(true);
    setError('');

    try {
      // Security: All console logs removed to prevent exposure of API endpoints and validation details
      
      const response = await fetch(`${API_BASE_URL}/api/auth/validate-key`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ api_key: encryptionKey.trim() })
      });

      if (response.status === 200) {
        const data = await response.json(); // eslint-disable-line no-unused-vars
        
        // Key is valid - reset failed attempts and grant access
        setFailedAttempts(0);
        localStorage.removeItem('namhs_gate_lockout');
        setIsAuthenticated(true);

        // Persist session so operator doesn't have to re-enter key on refresh
        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
          key: encryptionKey.trim(),
          expiresAt: Date.now() + SESSION_EXPIRY_MS
        }));
        
        // Pass the validated key to parent component
        if (onKeyValidated) {
          onKeyValidated(encryptionKey.trim());
        }
      } else {
        // Failed attempt - increment counter
        const newAttempts = failedAttempts + 1;
        setFailedAttempts(newAttempts);
        
        // Check if we should trigger lockout (10 attempts = lockout)
        if (newAttempts >= 10) {
          const lockEndTime = Date.now() + 10800000; // 180 minutes from now
          setIsLocked(true);
          setLockoutEndTime(lockEndTime);
          setRemainingLockoutTime(10800);
          
          // Save to localStorage for persistence across page refreshes
          localStorage.setItem('namhs_gate_lockout', JSON.stringify({
            endTime: lockEndTime,
            attempts: newAttempts
          }));
          
          setError('You are now locked out. Please contact NAMHS IT Administrator.');
        } else {
          // Show error with attempts remaining
          const attemptsRemaining = 10 - newAttempts;
          if (response.status === 401 || response.status === 403) {
            setError(`Invalid encryption key. ${attemptsRemaining} ${attemptsRemaining === 1 ? 'attempt' : 'attempts'} remaining.`);
          } else {
            const errorData = await response.json().catch(() => null);
            setError(errorData?.detail || `Failed to validate key. ${attemptsRemaining} ${attemptsRemaining === 1 ? 'attempt' : 'attempts'} remaining.`);
          }
        }
      }
    } catch (error) {
      // Network errors also count as failed attempts
      const newAttempts = failedAttempts + 1;
      setFailedAttempts(newAttempts);
      
      // Check if we should trigger lockout
      if (newAttempts >= 10) {
        const lockEndTime = Date.now() + 10800000; // 180 minutes from now
        setIsLocked(true);
        setLockoutEndTime(lockEndTime);
        setRemainingLockoutTime(10800);
        
        localStorage.setItem('namhs_gate_lockout', JSON.stringify({
          endTime: lockEndTime,
          attempts: newAttempts
        }));
        
        setError('You are now locked out. Please contact NAMHS IT Administrator.');
      } else {
        const attemptsRemaining = 10 - newAttempts;
        setError(`Unable to connect to server. ${attemptsRemaining} ${attemptsRemaining === 1 ? 'attempt' : 'attempts'} remaining.`);
      }
    } finally {
      setIsValidating(false);
    }
  };

  // ============================================================================
  // SILENT RE-VALIDATION LOADING STATE
  // ============================================================================
  // Show a minimal loading screen while the saved session is being validated
  // against the backend on page mount. This prevents a flash of the gate UI.
  if (isValidating && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-8 text-center">
          <div className="flex justify-center mb-6">
            <img src="/assets/Header/NAMHS logo clean.png" alt="NAMHS Logo" className="h-24 w-auto" />
          </div>
          <div className="flex items-center justify-center space-x-3 text-gray-600">
            <svg className="animate-spin h-5 w-5 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-sm font-medium">Verifying your session...</span>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // DEBUG MODE BYPASS
  // ============================================================================
  // If DEBUG_MODE is enabled, bypass the gate entirely
  if (DEBUG_MODE) {
    console.log('🔓 [GATE] DEBUG MODE ENABLED - Bypassing encryption gate');
    // Pass a dummy key for development
    if (onKeyValidated) {
      onKeyValidated('DEBUG_MODE_BYPASS');
    }
    return <>{children}</>;
  }

  // If authenticated, show the main application
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // Show the encryption key gate
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4 relative">
      {/* HIPAA Compliance Badge - Bottom Right 
          TODO: RE-ENABLE AFTER ACHIEVING HIPAA COMPLIANCE
          Temporarily hidden while working on HIPAA compliance certification.
          To re-enable: uncomment the div below
      */}
      {/* <div className="fixed bottom-4 right-4 z-50">
        <img 
          src="/assets/Footer/hipaa-blue.png" 
          alt="HIPAA Compliant" 
          className="h-20 w-auto opacity-90"
        />
      </div> */}

      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-8">
        {/* Logo Header */}
        <div className="flex justify-center mb-8">
          <img 
            src="/assets/Header/NAMHS logo clean.png" 
            alt="NAMHS Logo" 
            className="h-24 w-auto"
          />
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Secure Access
          </h1>
          <p className="text-gray-600">
            Please enter your encryption key to access the patient referral portal
          </p>
        </div>

        {/* Encryption Key Form */}
        <form onSubmit={validateEncryptionKey} className="space-y-6">
          {/* Key Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Encryption Key *
            </label>
            <div className="relative">
              <input
                type={showKey ? "text" : "password"}
                value={encryptionKey}
                onChange={(e) => {
                  setEncryptionKey(e.target.value);
                  if (!isLocked) {
                    setError(''); // Clear error on input (unless locked)
                  }
                }}
                placeholder="Enter your encryption key"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 pr-12 ${
                  error ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isValidating || isLocked}
                autoFocus
              />
              {/* Show/Hide Toggle */}
              <button
                type="button"
                onClick={() => setShowKey(!showKey)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showKey ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Lockout Warning */}
          {isLocked && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-800">Account Locked</p>
                  <p className="text-sm text-red-700 mt-1">
                    You are now locked out. Please contact <span className="font-bold">NAMHS IT Administrator</span> for assistance.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {!isLocked && error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-red-800">{error}</span>
              </div>
            </div>
          )}

          {/* Attempts Warning (show when 5 or fewer attempts remain) */}
          {!isLocked && failedAttempts > 0 && failedAttempts >= 5 && failedAttempts < 10 && !error && (
            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-yellow-800">
                    ⚠️ Warning: {10 - failedAttempts} {10 - failedAttempts === 1 ? 'attempt' : 'attempts'} remaining
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    After 10 failed attempts, you'll be locked out for 180 minutes and must contact NAMHS IT Administrator.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isValidating || !encryptionKey.trim() || isLocked}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors duration-200 ${
              isValidating || !encryptionKey.trim() || isLocked
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-primary-600 text-white hover:bg-primary-700'
            }`}
          >
            {isLocked ? (
              <span className="flex items-center justify-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                Locked Out
              </span>
            ) : isValidating ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Validating...
              </span>
            ) : (
              'Access Portal'
            )}
          </button>
        </form>

        {/* Help Text */}
        <div className="mt-6 text-center space-y-2">
          <p className="text-sm text-gray-500">
            Don't have an encryption key? Contact NAMHS's Development Team for assistance.
          </p>
          {localStorage.getItem(SESSION_STORAGE_KEY) && (
            <button
              type="button"
              onClick={() => {
                localStorage.removeItem(SESSION_STORAGE_KEY);
                setEncryptionKey('');
                setError('Session cleared. Please enter your encryption key.');
              }}
              className="text-xs text-gray-400 hover:text-gray-600 underline"
            >
              Clear saved session
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default EncryptionKeyGate;
